// "Hoy 20:23 hs" — la fecha de una creación, escrita como la lee alguien que generó hace
// un rato.
//
// Vivía copiada en `GeneratedImagesHistory` y en `GeneratedImagesInline`, palabra por
// palabra. Se extrajo al sumar un tercer consumidor —la columna de resultados del modo,
// que ahora rotula cada imagen con su hora
// (ADR-2026-09-04-la-columna-de-resultados-va-en-una-grilla-plana)—: tres copias del
// mismo calendario se desincronizan en el primer retoque de copy.
//
// Escala por cercanía, que es como se busca una creación propia: hoy / ayer / anteayer /
// el día de la semana (dentro de la semana) / día y mes.

const DIAS = [
  'Domingo',
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
];

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

/**
 * Sólo la hora, en 24h — "20:25 hs".
 *
 * La usa la columna de resultados para el rótulo de una card cuyo GRUPO ya dice el día
 * (ADR-2026-09-06-la-columna-de-resultados-agrupa-por-fecha): abajo de un "Hoy", nueve
 * cards repitiendo "Hoy" es ruido.
 *
 * @param fecha ISO de la base (`created_at`) o epoch en ms (las tandas de la sesión).
 */
export function horaDe(fecha: string | number): string {
  const date = new Date(fecha);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getHours().toString().padStart(2, '0')}:${date
    .getMinutes()
    .toString()
    .padStart(2, '0')} hs`;
}

/** @param fecha ISO de la base (`created_at`) o epoch en ms (las tandas de la sesión). */
export function fechaSmart(fecha: string | number): string {
  const date = new Date(fecha);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();

  // A medianoche, para comparar DÍAS y no instantes.
  const hoy = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const ayer = new Date(hoy);
  ayer.setDate(ayer.getDate() - 1);
  const anteayer = new Date(hoy);
  anteayer.setDate(anteayer.getDate() - 2);
  const haceUnaSemana = new Date(hoy);
  haceUnaSemana.setDate(haceUnaSemana.getDate() - 7);

  const dia = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const hora = horaDe(fecha);

  if (dia.getTime() === hoy.getTime()) return `Hoy ${hora}`;
  if (dia.getTime() === ayer.getTime()) return `Ayer ${hora}`;
  if (dia.getTime() === anteayer.getTime()) return `Anteayer ${hora}`;
  if (dia >= haceUnaSemana) return `${DIAS[date.getDay()]} ${hora}`;
  return `${date.getDate()} de ${MESES[date.getMonth()]}`;
}

export default fechaSmart;

/** Los meses en Mayúscula: encabezan un grupo ("Julio 2026"), no van en medio de una frase. */
const MESES_TITULO = MESES.map((m) => m[0].toUpperCase() + m.slice(1));

/**
 * A qué GRUPO de la columna de resultados pertenece una fecha —
 * ADR-2026-09-06-la-columna-de-resultados-agrupa-por-fecha.
 *
 * Escalera de CALENDARIO y no de ventanas móviles: "la semana pasada" y "el mes pasado"
 * son lo que dice alguien mirando un almanaque, no "hace entre 7 y 14 días". Las semanas
 * arrancan el LUNES.
 *
 * Es una cascada de primer match, y ahí está lo que la hace no tener huecos aunque los
 * rangos se pisen: con hoy = jueves 1/10, el lunes de la semana es el 28/9, así que el
 * 30/9 cae en "Esta semana" —matchea antes— y el 15/9 en "El mes pasado". Cada escalón
 * es "todo lo que sea >= este límite y no haya matcheado antes", y los límites bajan
 * siempre, así que entre uno y el siguiente no queda nada suelto.
 *
 * `clave` es lo que corta la lista (la grilla ya viene de lo más nuevo a lo más viejo:
 * se abre grupo nuevo cuando la clave cambia) y `titulo` lo que se dibuja. Van separados
 * porque los meses viejos comparten forma pero no grupo: "Julio 2026" y "Julio 2025" son
 * dos claves.
 *
 * @param fecha ISO de la base (`created_at`) o epoch en ms (las tandas de la sesión).
 */
export function grupoDeFecha(fecha: string | number): {
  clave: string;
  titulo: string;
} {
  const date = new Date(fecha);
  // Sin fecha no hay grupo. Devolver vacío y no inventar uno: una fila rota no puede
  // arrastrar a las demás a un encabezado que miente.
  if (Number.isNaN(date.getTime())) return { clave: '', titulo: '' };
  const now = new Date();

  // A medianoche, para comparar DÍAS y no instantes.
  const hoy = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const ayer = new Date(hoy);
  ayer.setDate(ayer.getDate() - 1);
  // El lunes de esta semana. `(getDay() + 6) % 7` convierte domingo=0 en 6, que es lo
  // que hace que un domingo pertenezca a la semana que arrancó el lunes ANTERIOR y no
  // que abra una nueva.
  const lunesDeEstaSemana = new Date(hoy);
  lunesDeEstaSemana.setDate(hoy.getDate() - ((hoy.getDay() + 6) % 7));
  const lunesDeLaSemanaPasada = new Date(lunesDeEstaSemana);
  lunesDeLaSemanaPasada.setDate(lunesDeLaSemanaPasada.getDate() - 7);
  const primeroDeEsteMes = new Date(now.getFullYear(), now.getMonth(), 1);
  const primeroDelMesPasado = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const dia = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (dia.getTime() === hoy.getTime()) return { clave: 'hoy', titulo: 'Hoy' };
  if (dia.getTime() === ayer.getTime()) return { clave: 'ayer', titulo: 'Ayer' };
  if (dia >= lunesDeEstaSemana)
    return { clave: 'esta-semana', titulo: 'Esta semana' };
  if (dia >= lunesDeLaSemanaPasada)
    return { clave: 'semana-pasada', titulo: 'La semana pasada' };
  if (dia >= primeroDeEsteMes) return { clave: 'este-mes', titulo: 'Este mes' };
  if (dia >= primeroDelMesPasado)
    return { clave: 'mes-pasado', titulo: 'El mes pasado' };
  return {
    clave: `${date.getFullYear()}-${date.getMonth()}`,
    titulo: `${MESES_TITULO[date.getMonth()]} ${date.getFullYear()}`,
  };
}
